import { uploadCredentialsService } from '@src/services/UploadCredentialsService';
import { LogManager } from '@src/utility/Logger';

import { extractAwsErrorDetails } from './AwsErrorExtractor';
import { safeVideoExtension } from './mediaValidation/filename';
import { performMultipartUpload } from './MultipartCore';

// eslint-disable-next-line @typescript-eslint/consistent-type-imports -- runtime import breaks Jest (import.meta.url in UploadWorkerProvider)
import type { UploadWorkerProvider } from './UploadWorkerProvider';
import type { ServiceErrorKind } from '@src/services/UploadCredentialsService';
import type {
  S3UploadWorkerErrorMessage,
  S3UploadWorkerOutboundMessage,
} from '@src/workers/s3UploadMessages';

/**
 * Custom error class for upload credential failures.
 * Allows callers to distinguish authentication errors from other credential issues.
 */
export class UploadCredentialError extends Error {
  readonly kind: ServiceErrorKind;
  readonly isAuthError: boolean;
  readonly userMessage: string;

  constructor(kind: ServiceErrorKind, message: string, cause?: unknown) {
    super(message);
    this.name = 'UploadCredentialError';
    this.kind = kind;
    this.isAuthError = kind === 'unauthorized';
    this.userMessage = this.isAuthError
      ? 'Authentication required. Please log in again.'
      : 'Failed to get upload credentials. Please try again.';
    this.cause = cause;
  }
}

/**
 * Custom error class for worker upload failures.
 * Preserves structured AWS error details from the worker message so the
 * classifier's `extractAwsErrorDetails` can read `Code`, `name`,
 * `$response.statusCode`, and `$metadata` without any changes.
 */
export class WorkerUploadError extends Error {
  readonly Code?: string;
  readonly $response?: { statusCode?: number };
  readonly $metadata?: { requestId?: string; attempts?: number };

  constructor(message: string, workerData: S3UploadWorkerErrorMessage) {
    super(message);
    this.name = workerData.errorName ?? 'WorkerUploadError';
    if (workerData.errorCode) this.Code = workerData.errorCode;
    if (workerData.httpStatus)
      this.$response = { statusCode: workerData.httpStatus };
    if (workerData.requestId || workerData.retryCount) {
      this.$metadata = {
        requestId: workerData.requestId,
        attempts: workerData.retryCount,
      };
    }
  }
}

// Baseline concurrency; may tune later (kept constant for now)
const UPLOAD_THREADS = 5;

export interface UploadOptions {
  onUploadProgress?: (progress: number, done: boolean, total?: number) => void;
  authToken?: string;
  offloadToWorker?: boolean;
  /**
   * Milliseconds to wait for the worker to acknowledge (via a 'started' message) after posting
   * the start message. If the timeout elapses, the upload falls back to the main‑thread path.
   * Defaults to 4000ms.
   */
  workerStartupTimeoutMs?: number;
  /**
   * Called once when the upload mechanism has been definitively chosen.
   * Values:
   *  - 'worker' when worker-based multipart took over.
   *  - 'main-thread' when falling back to main thread AWS SDK multipart.
   */
  onResolvedMechanism?: (mechanism: 'worker' | 'main-thread') => void;
  /** Aborts the multipart upload (S3 drops the uploaded parts) and rejects. */
  signal?: AbortSignal;
}

// Logger
const uploadLogger = LogManager.getLogger('upload:multipart');

/** Run `abort` now if `signal` already fired, otherwise when it fires. */
function abortOn(
  signal: AbortSignal | undefined,
  abort: (() => void) | undefined,
): void {
  if (!signal || !abort) return;
  if (signal.aborted) abort();
  else signal.addEventListener('abort', abort, { once: true });
}

function computePartSizeBytes(fileSize: number): number {
  const MB = 1024 * 1024;
  const MAX_PARTS = 10000;
  if (fileSize <= 128 * MB) return 8 * MB;
  if (fileSize <= 512 * MB) return 12 * MB;
  if (fileSize <= 2 * 1024 * MB) return 16 * MB;
  if (fileSize <= 5 * 1024 * MB) return 32 * MB;
  const minPartSize = Math.ceil(fileSize / MAX_PARTS);
  const targetParts = 1500;
  const derived = Math.max(Math.ceil(fileSize / targetParts), minPartSize);
  if (minPartSize > 128 * MB) return minPartSize;
  const clamped = Math.min(Math.max(derived, 32 * MB, minPartSize), 128 * MB);
  return clamped;
}

/** Test helper to expose part size computation logic
 * @internal
 */

export const __test_computePartSizeBytes = computePartSizeBytes;

export function renameFile(file: File, newBasename: string): File {
  // Sanitize the extension before it becomes part of the S3 key
  // (`externalUploads/{orgId}/{storyId}.{ext}`). Falls back to `mp4` for names
  // with no usable extension — story videos are validated to MP4/MOV upstream.
  const newName = `${newBasename}.${safeVideoExtension(file.name)}`;
  return new File([file], newName, {
    type: file.type,
    lastModified: file.lastModified,
  });
}

async function tryStartWorkerUpload(
  file: File,
  key: string,
  showcaseId: string | null | undefined,
  options: UploadOptions | undefined,
  resolve: () => void,
  reject: (reason?: unknown) => void,
  workerProvider: UploadWorkerProvider,
): Promise<{ started: boolean; controller?: { abort: () => void } }> {
  const wantWorker = options?.offloadToWorker !== false;
  if (!wantWorker) return { started: false };
  if (!showcaseId) return { started: false };
  const worker = workerProvider.get();
  if (!worker) return { started: false };

  const credentialsResult = await uploadCredentialsService.fetch(
    showcaseId,
    options?.authToken,
  );
  if (!credentialsResult.ok) {
    // Credential failure - reject immediately (no fallback, main-thread would fail identically)
    uploadLogger.error('credentials-failed', {
      errorKind: credentialsResult.error.kind,
      message: credentialsResult.error.message,
      status: credentialsResult.error.status,
    });
    throw new UploadCredentialError(
      credentialsResult.error.kind,
      credentialsResult.error.message,
      credentialsResult.error.cause,
    );
  }

  const partSizeBytes = computePartSizeBytes(file.size);
  const partSizeMB = Math.round(partSizeBytes / (1024 * 1024));
  const id = `${Date.now()}_${Math.random().toString(36).slice(2)}`;
  const startupTimeoutMs = options?.workerStartupTimeoutMs ?? 4000;
  let startedAck = false;

  // Attach streaming listener BEFORE posting start so no messages are missed.
  const onMessage = ({ data }: MessageEvent<S3UploadWorkerOutboundMessage>) => {
    if (!data || typeof data !== 'object' || !('type' in data)) return;
    if (typeof data.type !== 'string') return;
    if (data.id !== id) return;
    if (data.type === 'progress') {
      options?.onUploadProgress?.(data.progress, false, data.total);
    } else if (data.type === 'complete') {
      uploadLogger.info('upload-s3-complete', {
        mechanism: 'worker',
        key,
        fileName: file.name,
      });
      options?.onUploadProgress?.(100, true, file.size);
      worker.removeEventListener('message', onMessage);
      worker.removeEventListener('error', onError);
      resolve();
    } else if (data.type === 'error' || data.type === 'aborted') {
      worker.removeEventListener('message', onMessage);
      worker.removeEventListener('error', onError);
      if (data.type === 'aborted' && options?.signal?.aborted) {
        uploadLogger.info('upload-aborted', { mechanism: 'worker', key });
        reject(new Error('Upload aborted by user'));
        return;
      }
      const errorMsg =
        data.type === 'aborted'
          ? 'Upload aborted by user'
          : data.type === 'error'
            ? data.message
            : 'unknown';
      // Upload already in progress and failed inside worker – cannot fallback; critical error.
      uploadLogger.error('worker-upload-error', {
        type: data.type,
        message: errorMsg,
        fileName: file.name,
        sizeBytes: file.size,
        showcaseId,
        key,
        // Enhanced diagnostics (only present for error type)
        ...(data.type === 'error' && {
          errorName: data.errorName,
          errorCode: data.errorCode,
          httpStatus: data.httpStatus,
          requestId: data.requestId,
          retryCount: data.retryCount,
          rawError: data.rawError,
        }),
      });
      reject(
        data.type === 'error'
          ? new WorkerUploadError(errorMsg, data)
          : new Error(errorMsg),
      );
    }
  };
  worker.addEventListener('message', onMessage);
  const onError = (ev: ErrorEvent) => {
    uploadLogger.error('worker-error', {
      message: ev.message,
      file: file.name,
    });
    worker.removeEventListener('message', onMessage);
    reject(new Error(`Worker crashed: ${ev.message}`));
  };
  worker.addEventListener('error', onError);

  // Post start message once. The worker responds with 'started', then streams
  // progress/complete/error messages which are handled by onMessage above.
  try {
    worker.postMessage({
      type: 'start',
      id,
      file,
      options: {
        bucket: credentialsResult.value.bucket,
        region: credentialsResult.value.region,
        key,
        credentials: credentialsResult.value.credentials,
        partSizeMB,
        queueSize: UPLOAD_THREADS,
      },
    });
  } catch (err) {
    worker.removeEventListener('message', onMessage);
    worker.removeEventListener('error', onError);
    // Start message failed to post; will fallback to main-thread.
    uploadLogger.error('worker-start-post-failed', {
      message: String(err),
      nonFatal: true,
      action: 'fallback-to-main-thread',
    });
    return { started: false };
  }

  const handshakeOk: boolean = await new Promise(resolveHandshake => {
    const timer = setTimeout(() => {
      if (!startedAck) {
        // Timeout establishing worker handshake; fallback to main-thread path.
        uploadLogger.error('worker-start-timeout', {
          file: file.name,
          sizeBytes: file.size,
          showcaseId,
          timeoutMs: startupTimeoutMs,
          nonFatal: true,
          action: 'fallback-to-main-thread',
        });
        worker.removeEventListener('message', onMessage);
        worker.removeEventListener('error', onError);
        try {
          worker.postMessage({ type: 'abort', id });
        } catch {
          /* ignore */
        }
        resolveHandshake(false);
      }
    }, startupTimeoutMs);

    const handshakeListener = ({
      data,
    }: MessageEvent<S3UploadWorkerOutboundMessage>) => {
      if (!data || typeof data !== 'object' || !('type' in data)) return;
      if (data.id !== id) return;
      if (data.type === 'started') {
        startedAck = true;
        clearTimeout(timer);
        worker.removeEventListener('message', handshakeListener);
        resolveHandshake(true);
      } else if (data.type === 'error' || data.type === 'aborted') {
        clearTimeout(timer);
        worker.removeEventListener('message', handshakeListener);
        worker.removeEventListener('message', onMessage);
        worker.removeEventListener('error', onError);
        // Worker reported error/abort before start; fallback to main-thread path.
        uploadLogger.error('worker-start-failed-initial', {
          type: data.type,
          file: file.name,
          showcaseId,
          key,
          nonFatal: true,
          action: 'fallback-to-main-thread',
        });
        resolveHandshake(false);
      }
    };
    worker.addEventListener('message', handshakeListener);
  });

  if (!handshakeOk) return { started: false };

  // Mechanism resolved: worker path.
  uploadLogger.info('upload-mechanism-resolved', {
    mechanism: 'worker',
    key,
    fileName: file.name,
    fileSizeBytes: file.size,
  });
  try {
    options?.onResolvedMechanism?.('worker');
  } catch {
    /* ignore */
  }

  return {
    started: true,
    controller: {
      abort: () => {
        try {
          worker.postMessage({ type: 'abort', id });
        } catch {
          /* ignore */
        }
      },
    },
  };
}

export class UploadService {
  constructor(private readonly workerProvider: UploadWorkerProvider) {}

  async uploadFileToS3ExternalUploads(
    file: File,
    key: string,
    showcaseId: string | null | undefined,
    options?: UploadOptions,
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      (async () => {
        try {
          const workerAttempt = await tryStartWorkerUpload(
            file,
            key,
            showcaseId,
            options,
            resolve,
            reject,
            this.workerProvider,
          );
          if (workerAttempt.started) {
            abortOn(options?.signal, workerAttempt.controller?.abort);
            return;
          }
        } catch (err) {
          // tryStartWorkerUpload threw (e.g., UploadCredentialError) - reject immediately
          reject(err);
          return;
        }
        try {
          if (!showcaseId) {
            resolve();
            return;
          }
          const credentialsResult = await uploadCredentialsService.fetch(
            showcaseId,
            options?.authToken,
          );
          if (!credentialsResult.ok) {
            uploadLogger.error('credentials-failed', {
              errorKind: credentialsResult.error.kind,
              message: credentialsResult.error.message,
              status: credentialsResult.error.status,
            });
            reject(
              new UploadCredentialError(
                credentialsResult.error.kind,
                credentialsResult.error.message,
                credentialsResult.error.cause,
              ),
            );
            return;
          }
          const partSizeBytes = computePartSizeBytes(file.size);
          const partSizeMB = Math.round(partSizeBytes / (1024 * 1024));
          // Mechanism resolved (fallback main-thread) – invoke once if not already called by worker path.
          uploadLogger.info('upload-mechanism-resolved', {
            mechanism: 'main-thread',
            key,
            fileName: file.name,
            fileSizeBytes: file.size,
          });
          try {
            options?.onResolvedMechanism?.('main-thread');
          } catch {
            /* ignore */
          }

          const controller = performMultipartUpload({
            bucket: credentialsResult.value.bucket,
            region: credentialsResult.value.region,
            key,
            file,
            contentType: file.type,
            credentials: credentialsResult.value.credentials,
            queueSize: UPLOAD_THREADS,
            partSizeMB,
            onProgress: p => {
              options?.onUploadProgress?.(p.percent, false, p.total);
            },
          });
          abortOn(options?.signal, controller.abort);
          controller.promise
            .then(() => {
              uploadLogger.info('upload-s3-complete', {
                mechanism: 'main-thread',
                key,
                fileName: file.name,
              });
              options?.onUploadProgress?.(100, true, file.size);
              resolve();
            })
            .catch(err => {
              if (options?.signal?.aborted) {
                uploadLogger.info('upload-aborted', {
                  mechanism: 'main-thread',
                  key,
                });
                reject(err);
                return;
              }
              const errorDetails = extractAwsErrorDetails(err);
              uploadLogger.error('main-thread-upload-error', {
                message: errorDetails.message,
                fileName: file.name,
                sizeBytes: file.size,
                showcaseId,
                key,
                errorName: errorDetails.errorName,
                errorCode: errorDetails.errorCode,
                httpStatus: errorDetails.httpStatus,
                requestId: errorDetails.requestId,
                retryCount: errorDetails.retryCount,
                rawError: errorDetails.rawError,
              });
              reject(err);
            });
        } catch (err) {
          const errorDetails = extractAwsErrorDetails(err);
          uploadLogger.error('main-thread-upload-init-failed', {
            message: errorDetails.message,
            fileName: file.name,
            sizeBytes: file.size,
            showcaseId,
            errorName: errorDetails.errorName,
            errorCode: errorDetails.errorCode,
            httpStatus: errorDetails.httpStatus,
            requestId: errorDetails.requestId,
            retryCount: errorDetails.retryCount,
            rawError: errorDetails.rawError,
          });
          if (err instanceof Error) reject(err);
          else reject(new Error(String(err)));
        }
      })();
    });
  }
}

// Public interface surface for DI typing (keep minimal and stable)
export interface IUploadService {
  /**
   * Upload a file to S3 external uploads.  The user must have permission to upload to the target showcase.
   * @param file The file to upload.
   * @param key The S3 key for the uploaded file.  The user must have permission to upload to this key.
   * @param showcaseId The ID of the showcase.
   * @param options Optional upload options.
   */
  uploadFileToS3ExternalUploads(
    file: File,
    key: string,
    showcaseId: string | null | undefined,
    options?: UploadOptions,
  ): Promise<void>;
}
// Class is structurally compatible; keep explicit interface for DI typing
